import {
  useGetOne,
  useGetResourceLabel,
  useListContext,
  useTranslate,
} from 'ra-core';
import { useParams } from 'react-router-dom';
import { DataTable } from '../../components/admin/data-table';
import { Error as ErrorPage } from '../../components/admin/error';
import { Loading } from '../../components/admin/loading';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { MoonstarList } from '../Moonstar/MoonstarList';
import { MoonstarListActions } from '../Moonstar/MoonstarListActions';
import {
  ChecklistDateRangeFilter,
  DEFAULT_CHECKLIST_DATE_RANGE_OPTION,
  getPresetRangeFilter,
} from './ChecklistsListFilters';
import { ChecklistPercentageField } from './ChecklistMetricField';
import { getChecklistDetailsPath } from './checklistNavigation';

export interface ChecklistUnitOverview {
  id: number;
  unitId: number;
  unitName: string;
  unitManagerName?: string | null;
  completionRatio: number | null;
  onTimeRatio: number | null;
  overdueTasksCount: number;
  missingPhotosCount: number | null;
}

export function ChecklistOverviewPage() {
  const { id } = useParams<{ id: string }>();
  const translate = useTranslate();
  const getResourceLabel = useGetResourceLabel();
  const checklist = useGetOne<{ id: string; title: string }>('checklists', {
    id,
  });

  if (checklist.isPending) {
    return <Loading />;
  }

  if (checklist.isError) {
    return (
      <ErrorPage
        resetErrorBoundary={() => void checklist.refetch()}
        error={checklist.error}
      />
    );
  }

  const filters = [
    <ChecklistDateRangeFilter
      key="date-range"
      label="moonstar.checklists.filters.dateRange"
      alwaysOn
    />,
  ];

  return (
    <MoonstarList<ChecklistUnitOverview>
      empty={false}
      filters={filters}
      resource={`checklists/${id}/overview`}
      sort={{ field: 'completionRatio', order: 'ASC' }}
      filterDefaultValues={getPresetRangeFilter(
        DEFAULT_CHECKLIST_DATE_RANGE_OPTION
      )}
      title={[
        { label: getResourceLabel('checklists', 2), href: '/checklists' },
        { label: checklist.data.title, href: `/checklists/${id}` },
        translate('moonstar.checklists.overview.title'),
      ]}
      actions={<MoonstarListActions hideCreate />}
    >
      <ChecklistOverviewTable checklistId={id!} />
    </MoonstarList>
  );
}

function ChecklistOverviewTable({ checklistId }: { checklistId: string }) {
  const { filterValues } = useListContext();

  return (
    <DataTable<ChecklistUnitOverview>
      bulkActionButtons={false}
      empty={<NoActiveUnits />}
      rowClick={(_id, _resource, record) =>
        getChecklistDetailsPath(checklistId, record.unitId, filterValues)
      }
    >
      <DataTable.Col<ChecklistUnitOverview>
        source="unitName"
        label="resources.checklists.fields.unitName"
        disableSort
      />
      <DataTable.Col<ChecklistUnitOverview>
        source="unitManagerName"
        label="resources.checklists.fields.unitManagerName"
        disableSort
        render={(record) => record.unitManagerName || '—'}
      />
      <DataTable.Col<ChecklistUnitOverview>
        source="completionRatio"
        label="resources.checklists.fields.completionRatio"
        disableSort
        render={(record) => (
          <ChecklistPercentageField value={record.completionRatio} />
        )}
      />
      <DataTable.Col<ChecklistUnitOverview>
        source="onTimeRatio"
        label="resources.checklists.fields.onTimeRatio"
        disableSort
        render={(record) => (
          <ChecklistPercentageField value={record.onTimeRatio} />
        )}
      />
      <DataTable.Col<ChecklistUnitOverview>
        source="overdueTasksCount"
        label="resources.checklists.fields.overdueTasksCount"
        disableSort
      />
      <DataTable.Col<ChecklistUnitOverview>
        source="missingPhotosCount"
        label="resources.checklists.fields.missingPhotosCount"
        disableSort
        render={(record) => (
          <MissingPhotosField value={record.missingPhotosCount} />
        )}
      />
    </DataTable>
  );
}

function MissingPhotosField({ value }: { value: number | null }) {
  return (
    <span
      className={
        value !== null && value > 0 ? 'font-bold text-destructive' : undefined
      }
    >
      {value ?? '—'}
    </span>
  );
}

function NoActiveUnits() {
  const translate = useTranslate();

  return (
    <Alert>
      <AlertDescription>
        {translate('moonstar.checklists.overview.noActiveUnits')}
      </AlertDescription>
    </Alert>
  );
}
